/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { Sub } from '../../../../../shared/state/state.abstract.ts';
import {
  BehaviorSubject,
  delay,
  filter,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  pairwise,
  share,
  switchMap,
  tap,
  zip,
} from 'rxjs';
import { IOrderCreation } from '../../../create/states/orderDetails/orderCreation.state.ts';
import equal from 'fast-deep-equal/react';
import { navBlocker } from '../../../../../shared/components/navigation/blocker/nav.blocker.ts';
import {
  OrderDeliveryCostRes,
  orderService,
  ProductDataRes,
  ProductPriceAndDiscountByQuantityRes,
  ProductPriceAndDiscountByVatRes,
  TaxesByDateRes,
} from '../../../services/order.service.ts';
import { responseHandler } from '../../../../../shared/responseHandler/responseHandler.ts';
import { orderListState } from '../orderList.state.ts';
import {
  CreatedOrdersListRes,
  editOrderService,
  IPosition,
  OrderDetailsDataRes,
  SaveOrderRes,
} from '../../../services/editOrder.service.ts';
import {
  C_Discount_Kind,
  C_Kg_Representation,
  C_Order_Error_Code,
  C_Order_Type,
  C_Sale_Unit,
  C_Vat_Code,
  C_Virtual_Position,
  SaveOrderMutationVariables,
  Wa_OrderPositionInputs,
} from '../../../../../graphql/generatedModel.ts';
import { addTotalWeight, getTotalWeight } from '../../../loaders/editOrder.loader.ts';
import { snackbarService } from '../../../../../shared/components/snackbar/service/snackbar.service.ts';
import i18n from 'i18next';
import { companyConfigService } from '../../../../../shared/services/companyConfig/companyConfig.service.ts';
import { format } from 'date-fns';
import {
  additionalOptions$,
  getOrderTypeDescription,
  IAdditionalOptions,
  IMergePosition,
  initOrderDetailsState,
  IOrderDetailsState,
  IPrepareArgsToSave,
  TArticleFromProductList,
} from './orderDetails.state.ts';
import { localeFormatterHelper } from '../../../../../shared/helpers/formatter/localeFormatter.helper.ts';

export class SubImpl extends Sub<IOrderDetailsState> {
  private loading$ = new BehaviorSubject<boolean>(false);
  private shareLoading$: Observable<boolean> = this.loading$.pipe(share());
  private saveLoading$ = new BehaviorSubject<boolean>(false);
  private shareSaveLoading$: Observable<boolean> = this.saveLoading$.pipe(share());
  private warnings$ = new BehaviorSubject<IOrderCreation['warnings'] | undefined>(undefined);
  private shareWarnings$: Observable<IOrderCreation['warnings'] | undefined> = this.warnings$.pipe(
    share(),
  );
  private cellsLoading$ = new BehaviorSubject<string[]>([]); // Loading by cells
  private shareCellsLoading$ = this.cellsLoading$.pipe(share());
  private barcodeLoading$ = new BehaviorSubject<boolean>(false);
  private shareBarcodeLoading$: Observable<boolean> = this.barcodeLoading$.pipe(share());
  private calcPriceError$ = new BehaviorSubject<number[]>([]);
  private shareCalcPriceError$ = this.calcPriceError$.pipe(share());
  private editedCells: Set<string> = new Set(); // Cells edited manually (format - 'idPosition_cellName')
  private deliveryCostLoading$ = new BehaviorSubject<boolean>(false);
  protected shareDeliveryCostLoading$ = this.deliveryCostLoading$.pipe(share());

  protected actionHandlers(): Observable<IOrderDetailsState> {
    return merge(
      this.queryData(),
      this.updateState(),
      this.simpleCell(),
      this.productCell(),
      this.vatCell(),
      this.quantityCell(),
      this.updatePosition(),
      this.addPosition(),
      this.addPositionByBarcode(),
      this.mergePositions(),
      this.resetOrderDataChanges(),
      this.deletePosition(),
      this.saveOrder(),
      this.updateLastEmailDetails(),
      this.selectPosition(),
    ).pipe(
      tap((state) => {
        const dirty =
          state.action !== 'emptyData' && !equal(state.orderDataBackup, state.orderData);
        state.dirty = dirty;
        navBlocker.watch(dirty);
        this.stream$.next({ ...state, action: 'internalUpdate' });
      }),
      tap(({ action }) => action !== 'saveOrder' && this.loading$.next(false)),
    );
  }
  loading(): Observable<boolean> {
    return this.shareLoading$;
  }
  barcodeLoading(): Observable<boolean> {
    return this.shareBarcodeLoading$;
  }
  saveLoading(): Observable<boolean> {
    return this.shareSaveLoading$;
  }
  warnings(): Observable<IOrderCreation['warnings'] | undefined> {
    return this.shareWarnings$;
  }
  cellsLoading(): Observable<string[]> {
    return this.shareCellsLoading$;
  }
  deliveryCostLoading(): Observable<boolean> {
    return this.shareDeliveryCostLoading$;
  }
  calculationPriceError(): Observable<number[]> {
    return this.shareCalcPriceError$;
  }
  private updateState(): Observable<IOrderDetailsState> {
    return this.actionListener([
      'updateOrderData',
      'updatePositions',
      'updateState',
      'updateIsNotePinned',
    ]);
  }

  /* METHODS OF SECONDARY OPTIONS start */
  additionalOptions(): Observable<IAdditionalOptions> {
    return additionalOptions$.pipe(switchMap(() => merge(this.productList(), this.taxesList())));
  }
  private productList(): Observable<IAdditionalOptions> {
    return orderService.sub.filteredProductData().pipe(
      responseHandler<IAdditionalOptions['productData']>({
        errorReturnType: {
          productList: [],
          listSpecialAvailabilityStateForArticles: undefined,
          listSpecialAssortmentGroups: undefined,
        },
        customErrorHandler: () => 'errors.unknownWithReload',
      }),
      map((productData) => {
        const additionalOptionsState = additionalOptions$.getValue();
        additionalOptionsState.productData = productData;
        return additionalOptionsState;
      }),
    );
  }
  private taxesList(): Observable<IAdditionalOptions> {
    return orderService.sub.taxes().pipe(
      responseHandler<TaxesByDateRes>({
        errorReturnType: [],
      }),
      map((taxes) => {
        const additionalOptionsState = additionalOptions$.getValue();
        additionalOptionsState.taxes = taxes;
        return additionalOptionsState;
      }),
    );
  }
  /* METHODS OF SECONDARY OPTIONS end */

  /* MASTER DATA METHODS start */
  private queryData(): Observable<IOrderDetailsState> {
    return orderListState.sub.state().pipe(
      pairwise(),
      filter(
        ([prevV, currV]) =>
          (currV.action === 'selectedOrder' ||
            currV.action === 'deleteSelectedOrder' ||
            currV.action === 'deleteSelectedOrderFromList' ||
            currV.action === 'filter') &&
          (prevV.filter.date !== currV.filter.date ||
            prevV.selectedOrder?.orderId !== currV.selectedOrder?.orderId),
      ),
      switchMap(([prevListState, listState]) => {
        if (!listState.selectedOrder) {
          return of({ ...initOrderDetailsState, action: 'emptyData' } as IOrderDetailsState);
        }
        return zip(this.stream$, of(listState)).pipe(
          tap(([_, listState]) => {
            this.loading$.next(true);
            const customerId = listState.selectedOrder?.customerId as string;
            const orderId = listState.selectedOrder?.orderId as string;
            editOrderService.pub.orderDetailsData({ customerId, orderId });
            const {
              productData: { listSpecialAssortmentGroups, listSpecialAvailabilityStateForArticles },
            } = additionalOptions$.getValue();

            const orderType = listState.selectedOrder?.orderType as C_Order_Type;
            const date = listState.selectedOrder?.orderDate;
            const isDateChanged =
              prevListState.selectedOrder?.orderDate !== date ||
              !listSpecialAvailabilityStateForArticles;
            const isNewCustomer = prevListState.selectedOrder?.customerId !== customerId;
            orderService.pub.filteredProductData({
              ...((isNewCustomer || !listSpecialAssortmentGroups) && { customerId }),
              isDateChanged,
              date,
              orderType,
              listSpecialAssortmentGroups,
              listSpecialAvailabilityStateForArticles,
            });

            const prevDate = prevListState.selectedOrder?.orderDate;
            if (prevDate !== date) {
              orderService.pub.taxes({
                date: date,
              });
            }
          }),
          switchMap(([state, listState]) => {
            const orderTypeDescription = getOrderTypeDescription(
              listState?.selectedOrder as CreatedOrdersListRes[number],
            );
            return editOrderService.sub.orderDetailsData().pipe(
              responseHandler<OrderDetailsDataRes | undefined>({ errorReturnType: undefined }),
              map((data) => {
                if (
                  !data ||
                  !Object.keys(data.customerInfo).length ||
                  !Object.keys(data.orderData).length
                ) {
                  // in case of error
                  const emptyState = structuredClone(initOrderDetailsState);
                  emptyState.action = 'errorData';
                  return emptyState;
                }
                const { customerInfo, orderData } = data;
                const {
                  customerNo,
                  internalOrFullName,
                  informationTip,
                  deliveryAddress,
                  discountKindId,
                  isDeliverySplitting,
                  deliverySplittingPartsCount,
                  transportSector,
                  transportSectorId,
                  id,
                  ...contacts
                } = customerInfo;
                const orderDataWithWeight = addTotalWeight({ ...orderData, orderTypeDescription });
                return {
                  ...state,
                  customerInfo: {
                    customerNo,
                    internalOrFullName,
                    informationTip,
                    deliveryAddress,
                    discountKindId,
                    isDeliverySplitting,
                    deliverySplittingPartsCount,
                    transportSector,
                    transportSectorId,
                    id,
                    contacts,
                  },
                  orderData: orderDataWithWeight,
                  orderDataBackup: structuredClone(orderDataWithWeight),
                  selectedPos: null,
                  noData: false,
                  action: 'queryData',
                } as IOrderDetailsState;
              }),
            );
          }),
        );
      }),
    );
  }
  saveOrder(): Observable<IOrderDetailsState> {
    return this.actionListener('saveOrder').pipe(
      tap(() => {
        this.saveLoading$.next(true);
      }),
      switchMap((state) => zip(orderListState.sub.sharedValues(), of(state))),
      tap(([{ order }, state]) => {
        const args = this.prepareArgsToSave({
          state,
          orderId: order?.orderId as string,
          modificationOptions: state?.params,
        });
        editOrderService.pub.saveOrder(args);
      }),
      switchMap(([{ order, lastOrderInTable }, state]) => {
        return editOrderService.sub.saveOrder().pipe(
          responseHandler<SaveOrderRes>({
            customErrorHandler: ({ code }) => {
              this.saveLoading$.next(false);
              const needSecondRequest =
                code === C_Order_Error_Code.OEC3_QUANTITY_PER_LOT ||
                code === C_Order_Error_Code.OEC4_MIN_MAX ||
                code === C_Order_Error_Code.OEC5_SPLITTING ||
                code === C_Order_Error_Code.OEC6_NOT_ENOUGH_DAYS_TO_PRODUCE;
              const emptyPositionsError =
                code === C_Order_Error_Code.OEC2_EMPTY_POSITIONS ||
                code === C_Order_Error_Code.OEC1_EMPTY_ORDER;
              if (needSecondRequest) {
                this.warnings$.next(code);
              } else if (emptyPositionsError) {
                snackbarService.pub.show({
                  type: 'error',
                  content: i18n.t('order.empty_positions_create_warning'),
                });
              } else {
                snackbarService.pub.show({
                  type: 'error',
                  content: i18n.t('common.server_error'),
                });
              }
              return undefined;
            },
            errorReturnType: undefined,
            quite: true,
          }),
          filter((data) => {
            return !!data;
          }),
          map((result) => {
            this.saveLoading$.next(false);
            snackbarService.pub.show({
              type: 'success',
              content: i18n.t('common.successfully_saved'),
            });
            const isLeftGridDataChanged =
              state?.customerInfo?.customerNo !== result?.customerNo ||
              order?.orderTotal !== result?.orderTotal;
            if (order?.orderDate !== result?.orderDate) {
              orderListState.pub.deleteSelectedOrderFromList();
              if (lastOrderInTable) {
                return { ...initOrderDetailsState, action: 'emptyData' } as IOrderDetailsState;
              }
            } else if (isLeftGridDataChanged) {
              orderListState.pub.editSelectedOrder(result);
            }

            return {
              ...state,
              orderDataBackup: structuredClone(state.orderData),
              dirty: false,
            };
          }),
        );
      }),
    );
  }
  mergePositions(): Observable<IOrderDetailsState> {
    return this.actionListener('mergePositions').pipe(
      map((oldState) => {
        const state = structuredClone(oldState);
        const positions = state?.orderData?.positions || [];

        const { mergedPositions: updatedPositions, isChanged } = positions.reduce<IMergePosition>(
          (accumValues, pos) => {
            const { mergedPositions } = accumValues;
            let { isChanged } = accumValues;
            const position = pos as IPosition;

            const existingPositionIndex = mergedPositions.findIndex(
              (p) =>
                p.articleId === position?.articleId &&
                p.discount === position.discount &&
                p.indTextDeliveryNote === position.indTextDeliveryNote &&
                p.indTextProduction === position.indTextProduction &&
                p.price === position.price &&
                p.vatCode === position.vatCode,
            );

            if (existingPositionIndex !== -1) {
              if (mergedPositions[existingPositionIndex]) {
                isChanged = true;
                mergedPositions[existingPositionIndex].quantity! += position?.quantity || 0;
                mergedPositions[existingPositionIndex].total = this.calculateTotalDependsOnSaleUnit(
                  mergedPositions[existingPositionIndex],
                );
              }
            } else {
              mergedPositions.push({ ...position });
            }

            return { mergedPositions, isChanged };
          },
          { mergedPositions: [], isChanged: false },
        );

        if (isChanged) {
          state.orderData.positions = updatedPositions;
          return state;
        }

        // when nothing changed
        snackbarService.pub.show({
          type: 'info',
          content: i18n.t('order.nothing_to_merge'),
        });
        return { ...state, action: undefined };
      }),
    );
  }
  addPositionByBarcode(): Observable<IOrderDetailsState> {
    return this.actionListener('addPositionByBarcode').pipe(
      mergeMap((oldState) => {
        const state = structuredClone(oldState);
        const {
          articleInfo,
          price: priceFromBarcode,
          quantity,
        } = state?.params as {
          articleInfo: TArticleFromProductList;
          quantity: number;
          price: number;
        };

        const newPosition = {
          virtualPositionId: null,
          articleId: articleInfo?.id,
          articleNo: articleInfo?.articleNo,
          description: articleInfo?.label,
          quantity,
          price: 0,
          total: 0,
          discount: 0,
          vatCode: articleInfo?.vatCode || C_Vat_Code.VT5_ZERO,
          indTextDeliveryNote: null,
          indTextProduction: null,
          splittingPartsPreset: null,
          quantityPerLot: articleInfo?.qtyPerLot,
          minQuantity: null,
          maxQuantity: null,
          saleUnit: articleInfo?.saleUnit,
          weight: articleInfo?.weight,
          isChangeable: true,
          id: Date.now(),
          __reorder__: `(${articleInfo?.articleNo}) ${articleInfo?.label}`,
        };

        return zip(of(newPosition), orderListState.sub.sharedValues()).pipe(
          tap(([newPosition, customerData]) => {
            orderService.pub.productData({
              orderAndArticleProps: {
                orderDate: customerData.date,
                customerId: customerData.order?.customerId!,
                articleId: newPosition?.articleId!,
                vatCode: newPosition?.vatCode,
                quantity: Number(newPosition?.quantity),
              },
            });
            this.barcodeLoading$.next(true);
          }),
          switchMap(([newPosition]) => {
            return orderService.sub.productData().pipe(
              responseHandler<ProductDataRes | 'serverError'>({ errorReturnType: 'serverError' }),
              map((values) => {
                let updatedPos = { ...newPosition } as IPosition;
                if (values === 'serverError' && newPosition?.id) {
                  this.calcPriceError$.next([...this.calcPriceError$.value, newPosition?.id]);
                } else if (typeof values === 'object') {
                  updatedPos = {
                    ...newPosition,
                    ...values,
                    price: priceFromBarcode ? priceFromBarcode : values?.price,
                  } as IPosition;
                }
                updatedPos.total = this.calculateTotalDependsOnSaleUnit(updatedPos);
                state.orderData.positions = [...(state?.orderData?.positions || []), updatedPos];
                state.selectedPos = updatedPos;
                this.barcodeLoading$.next(false);
                return { ...state };
              }),
            );
          }),
        );
      }),
    );
  }
  addPosition(): Observable<IOrderDetailsState> {
    return this.actionListener(['addPosition', 'addDeliveryPosition']).pipe(
      delay(50), // fixes the problem if the cell is in edit mode
      switchMap((state) => {
        const updatedState = structuredClone(state);
        if (updatedState.action === 'addDeliveryPosition') {
          return this.getDeliveryCost({ state, force: true });
        } else {
          if (updatedState.selectedPos) {
            updatedState.orderData.positions?.push(updatedState.selectedPos);
          } else {
            const newPosition = {
              virtualPositionId: null,
              articleId: null,
              articleNo: null,
              description: null,
              quantity: null,
              price: 0,
              total: 0,
              discount: 0,
              vatCode: C_Vat_Code.VT5_ZERO,
              indTextDeliveryNote: null,
              indTextProduction: null,
              splittingPartsPreset: null,
              quantityPerLot: null,
              minQuantity: null,
              maxQuantity: null,
              saleUnit: null,
              weight: null,
              isChangeable: true,
              id: Date.now(),
              __reorder__: i18n.t('order.new_position'),
            };
            const deliveryPositionInd = updatedState.orderData.positions?.findIndex(
              (el) => el?.virtualPositionId,
            );
            if (typeof deliveryPositionInd === 'number' && deliveryPositionInd !== -1) {
              updatedState.orderData.positions?.splice(deliveryPositionInd, 0, newPosition);
            } else updatedState.orderData.positions?.push(newPosition);
            updatedState.selectedPos = newPosition;
          }
          return of(updatedState);
        }
      }),
    );
  }
  deletePosition(): Observable<IOrderDetailsState> {
    return this.actionListener(['deletePosition', 'deleteDeliveryPosition']).pipe(
      delay(50), // fixes the problem if the cell is in edit mode
      map((state) => {
        const updatedState = structuredClone(state);
        if (updatedState.action === 'deleteDeliveryPosition') {
          updatedState.orderData.positions = updatedState.orderData.positions?.filter(
            (pos) => !pos?.virtualPositionId,
          );
        } else {
          let selectedPos: IOrderDetailsState['selectedPos'] = null;
          updatedState.orderData.positions = updatedState.orderData.positions?.filter(
            (pos, i, arr) => {
              if (pos?.id === state.selectedPos?.id) {
                const nextPos = arr?.[i + 1];
                const prevPos = arr?.[i - 1];
                if (i === 0 && arr!.length > 1) {
                  selectedPos =
                    !nextPos?.virtualPositionId && nextPos?.isChangeable ? nextPos : null;
                }
                if (i !== 0) {
                  selectedPos =
                    (!nextPos?.virtualPositionId && nextPos?.isChangeable && nextPos) ||
                    (prevPos?.isChangeable ? prevPos : null);
                }
                return false;
              } else return true;
            },
          );
          updatedState.selectedPos = selectedPos;
        }
        return updatedState;
      }),
      switchMap((state) => {
        const prevSelectedPos = this.stream$.getValue().selectedPos;
        if (
          state.action === 'deletePosition' &&
          prevSelectedPos?.articleId &&
          prevSelectedPos?.quantity &&
          prevSelectedPos?.weight
        ) {
          this.stream$.next({ ...state, action: 'updateState' });
          return this.getDeliveryCost({ state }); // Recalculate delivery data
        } else return of(state);
      }),
    );
  }

  updatePosition(): Observable<IOrderDetailsState> {
    return this.actionListener('updatePosition').pipe(
      map((state) => {
        const updatedState = structuredClone(state);
        const { selectedPos } = state;
        updatedState.orderData.positions = updatedState.orderData.positions?.map((pos) => {
          if (pos?.id === selectedPos?.id) return selectedPos;
          return pos;
        });
        return updatedState;
      }),
    );
  }

  updateLastEmailDetails(): Observable<IOrderDetailsState> {
    return this.actionListener(['updateLastEmailDetails']).pipe(
      switchMap((state) => {
        return zip(of(state), orderListState.sub.sharedValues());
      }),
      filter(([state, { order }]) => state.params.dataRecordId === order?.orderId),
      map(([state]) => {
        const updatedState = structuredClone(state);
        updatedState.orderData.datetimeOfLastEmail = state.params.datetimeOfLastEmail;
        updatedState.orderDataBackup.datetimeOfLastEmail = state.params.datetimeOfLastEmail;
        return updatedState;
      }),
    );
  }
  selectPosition(): Observable<IOrderDetailsState> {
    return this.actionListener('selectPosition').pipe(delay(10));
  }
  /* MASTER DATA METHODS end */

  /* ORDER CELL METHODS start */
  private quantityCell(): Observable<IOrderDetailsState> {
    return this.actionListener('quantityCell').pipe(
      mergeMap((state) => {
        const { selectedPos, action, customerInfo } = state;
        const selectedPosOldValues = state.orderData.positions?.find(
          (pos) => pos?.id === selectedPos?.id,
        );
        this.preUpdateCell(selectedPos);
        const isNotEditableDiscount =
          customerInfo?.discountKindId === C_Discount_Kind.DK5_QUANTITY_DISCOUNT ||
          customerInfo?.discountKindId === C_Discount_Kind.DK6_QUANTITY_DISCOUNT_GROUPS;
        const shouldFetch = Boolean(
          selectedPos?.articleNo &&
            !(
              this.editedCells.has(`${selectedPos?.id}_priceCell`) &&
              (isNotEditableDiscount || this.editedCells.has(`${selectedPos?.id}_discountCell`))
            ),
        );
        return zip(of(selectedPos), orderListState.sub.sharedValues()).pipe(
          tap(([editablePos, { order, date }]) => {
            if (shouldFetch) {
              orderService.pub.productPriceAndDiscountByQuantity({
                orderAndArticleProps: {
                  articleId: editablePos?.articleId!,
                  customerId: order?.customerId!,
                  orderDate: date,
                  vatCode: editablePos?.vatCode!,
                  oldQuantity: Number(selectedPosOldValues?.quantity),
                  newQuantity: Number(editablePos?.quantity!),
                },
              });
              this.cellsLoading$.next([...this.cellsLoading$.value, `${editablePos?.id}_quantity`]);
            }
          }),
          switchMap(([editablePos]) => {
            if (shouldFetch) {
              return orderService.sub.productPriceAndDiscountByQuantity().pipe(
                responseHandler<ProductPriceAndDiscountByQuantityRes | 'serverError'>({
                  errorReturnType: 'serverError',
                }),
                map((values) => {
                  const { oldValues, newValues } =
                    (values as ProductPriceAndDiscountByQuantityRes) || {};

                  const shouldUpdatePrice =
                    (oldValues?.price ?? 0) === (selectedPosOldValues?.price ?? 0);
                  const shouldUpdateDiscount =
                    (oldValues?.discount ?? 0) === (selectedPosOldValues?.discount ?? 0);

                  this.cellsLoading$.next(
                    this.cellsLoading$.value.filter(
                      (item) => item !== `${editablePos?.id}_quantity`,
                    ),
                  );
                  if (!selectedPosOldValues) return;
                  const updatedPos = { ...selectedPosOldValues, quantity: editablePos?.quantity };
                  if (!this.editedCells.has(`${updatedPos?.id}_priceCell`)) {
                    if (values === 'serverError' && editablePos?.id) {
                      updatedPos.price = 0;
                      this.calcPriceError$.next([...this.calcPriceError$.value, editablePos.id]);
                      return updatedPos;
                    } else if (typeof values === 'object' && editablePos?.id && shouldUpdatePrice) {
                      updatedPos.price = newValues.price;
                      this.calcPriceError$.next(
                        this.calcPriceError$.value.filter((item) => item !== editablePos?.id),
                      );
                    }
                  }
                  if (
                    !this.editedCells.has(`${updatedPos?.id}_discountCell`) &&
                    typeof values === 'object' &&
                    shouldUpdateDiscount
                  ) {
                    updatedPos.discount = newValues.discount;
                  }
                  return updatedPos;
                }),
              );
            }
            return of(editablePos);
          }),
          filter((updatedPos) => !!updatedPos), // if position is deleted
          map((updatedPos) => {
            const state = this.stream$.getValue();
            updatedPos!.total = this.calculateTotalDependsOnSaleUnit(updatedPos!);
            state.orderData.positions = state.orderData.positions?.map((el) =>
              el?.id === updatedPos?.id ? updatedPos! : el,
            );
            this.editedCells.add(`${updatedPos?.id}_${action}`);
            return { ...state };
          }),
        );
      }),
      switchMap((state) => {
        if (state.selectedPos?.articleId) {
          this.stream$.next({ ...state, action: 'updateState' });
          return this.getDeliveryCost({ state }); // Recalculate delivery data
        } else return of(state);
      }),
    );
  }

  private resetOrderDataChanges(): Observable<IOrderDetailsState> {
    return this.actionListener('resetOrderDataChanges').pipe(
      delay(50), // fixes the problem if the cell is in edit mode
      map((state) => {
        const updatedState = structuredClone(state);
        const { orderDataBackup } = updatedState;
        updatedState.orderData = structuredClone(orderDataBackup);
        this.calcPriceError$.next([]);
        return updatedState;
      }),
    );
  }

  private simpleCell(): Observable<IOrderDetailsState> {
    return this.actionListener(['priceCell', 'discountCell', 'splittingCell']).pipe(
      map((state) => {
        const { selectedPos, action } = state;
        const updatedPos = structuredClone(selectedPos!);
        if (action === 'priceCell') {
          this.calcPriceError$.next(
            this.calcPriceError$.value.filter((item) => item !== updatedPos?.id),
          );
          updatedPos.total = this.calculateTotalDependsOnSaleUnit(updatedPos);
        }
        state.orderData.positions = state.orderData.positions?.map((el) =>
          el?.id === updatedPos?.id ? updatedPos : el,
        );
        state.selectedPos = updatedPos;
        this.editedCells.add(`${updatedPos?.id}_${action}`);
        return { ...state };
      }),
    );
  }

  private vatCell(): Observable<IOrderDetailsState> {
    return this.actionListener('vatCodeCell').pipe(
      mergeMap((state) => {
        const { selectedPos, customerInfo, action } = state;
        const selectedPosOldValues = state.orderData.positions?.find(
          (pos) => pos?.id === selectedPos?.id,
        );
        this.preUpdateCell(selectedPos);
        const isNotEditableDiscount =
          customerInfo?.discountKindId === C_Discount_Kind.DK5_QUANTITY_DISCOUNT ||
          customerInfo?.discountKindId === C_Discount_Kind.DK6_QUANTITY_DISCOUNT_GROUPS;
        const shouldFetch = Boolean(
          selectedPos?.articleNo &&
            !(
              this.editedCells.has(`${selectedPos?.id}_priceCell`) &&
              (isNotEditableDiscount || this.editedCells.has(`${selectedPos?.id}_discountCell`))
            ),
        );
        return zip(of(selectedPos), orderListState.sub.sharedValues()).pipe(
          tap(([editablePos, { order, date }]) => {
            if (shouldFetch) {
              orderService.pub.productPriceAndDiscountByVat({
                orderAndArticleProps: {
                  articleId: editablePos?.articleId!,
                  customerId: order?.customerId!,
                  orderDate: date,
                  quantity: Number(editablePos?.quantity),
                  oldVatCode: selectedPosOldValues?.vatCode!,
                  newVatCode: editablePos?.vatCode!,
                },
              });
              this.cellsLoading$.next([...this.cellsLoading$.value, `${editablePos?.id}_vatCode`]);
            }
          }),
          switchMap(([editablePos]) => {
            if (shouldFetch) {
              return orderService.sub.productPriceAndDiscountByVat().pipe(
                responseHandler<ProductPriceAndDiscountByVatRes | 'serverError'>({
                  errorReturnType: 'serverError',
                }),
                map((values) => {
                  const { oldValues, newValues } =
                    (values as ProductPriceAndDiscountByVatRes) || {};
                  const shouldUpdatePrice =
                    (oldValues?.price ?? 0) === (selectedPosOldValues?.price ?? 0);
                  const shouldUpdateDiscount =
                    (oldValues?.discount ?? 0) === (selectedPosOldValues?.discount ?? 0);
                  this.cellsLoading$.next(
                    this.cellsLoading$.value.filter(
                      (item) => item !== `${editablePos?.id}_vatCode`,
                    ),
                  );
                  if (!selectedPosOldValues) return;
                  const updatedPos = {
                    ...selectedPosOldValues,
                    vatCode: editablePos?.vatCode as C_Vat_Code,
                  };
                  if (!this.editedCells.has(`${updatedPos?.id}_priceCell`)) {
                    if (values === 'serverError' && editablePos?.id) {
                      updatedPos.price = 0;
                      updatedPos.total = 0;
                      this.calcPriceError$.next([...this.calcPriceError$.value, editablePos?.id]);
                      return updatedPos;
                    } else if (typeof values === 'object' && editablePos?.id && shouldUpdatePrice) {
                      updatedPos.price = newValues.price;
                      updatedPos.total = this.calculateTotalDependsOnSaleUnit(updatedPos);
                      this.calcPriceError$.next(
                        this.calcPriceError$.value.filter((item) => item !== editablePos?.id),
                      );
                    }
                  }
                  if (
                    !this.editedCells.has(`${editablePos?.id}_discountCell`) &&
                    typeof values === 'object' &&
                    shouldUpdateDiscount
                  ) {
                    updatedPos.discount = newValues.discount;
                  }
                  return updatedPos;
                }),
              );
            }
            return of(editablePos);
          }),
          filter((updatedPos) => !!updatedPos), // if position is deleted
          map((updatedPos) => {
            const state = this.stream$.getValue();
            state.orderData.positions = state.orderData.positions?.map((el) =>
              el?.id === updatedPos?.id ? updatedPos! : el,
            );
            this.editedCells.add(`${updatedPos?.id}_${action}`);
            return { ...state };
          }),
        );
      }),
    );
  }

  private productCell(): Observable<IOrderDetailsState> {
    return this.actionListener('descriptionCell').pipe(
      mergeMap(({ selectedPos, action }) => {
        this.preUpdateCell(selectedPos);
        return zip(of(selectedPos), orderListState.sub.sharedValues()).pipe(
          tap(([editablePos, customerData]) => {
            const position = this.stream$.value.selectedPos;
            orderService.pub.productData({
              orderAndArticleProps: {
                orderDate: customerData.date,
                customerId: customerData.order?.customerId!,
                articleId: editablePos?.articleId!,
                vatCode: position?.vatCode!,
                quantity: Number(position?.quantity),
              },
            });
            this.cellsLoading$.next([
              ...this.cellsLoading$.value,
              `${editablePos?.id}_description`,
            ]);
          }),
          switchMap(([editablePos]) => {
            return orderService.sub.productData().pipe(
              responseHandler<ProductDataRes | 'serverError'>({ errorReturnType: 'serverError' }),
              filter(() => {
                const state = this.stream$.getValue();
                this.cellsLoading$.next(
                  this.cellsLoading$.value.filter(
                    (item) => item !== `${editablePos?.id}_description`,
                  ),
                );
                return !!state.orderData.positions?.some((el) => el?.id === editablePos?.id);
              }), // if position is deleted
              map((values) => {
                const state = this.stream$.getValue();
                let updatedPos = state.orderData.positions?.find(
                  (pos) => pos?.id === editablePos?.id,
                )!;
                if (values === 'serverError' && editablePos?.id) {
                  updatedPos.price = 0;
                  this.calcPriceError$.next([...this.calcPriceError$.value, editablePos?.id]);
                } else if (typeof values === 'object' && editablePos?.id) {
                  updatedPos = { ...updatedPos, ...values } as IPosition;
                  this.calcPriceError$.next(
                    this.calcPriceError$.value.filter((item) => item !== editablePos?.id),
                  );
                }
                updatedPos.total = this.calculateTotalDependsOnSaleUnit(updatedPos);
                state.orderData.positions = state.orderData.positions?.map((el) =>
                  el?.id === updatedPos?.id ? updatedPos : el,
                );
                this.editedCells.add(`${editablePos?.id}_${action}`);
                this.editedCells.delete(`${editablePos?.id}_priceCell`);
                this.editedCells.delete(`${editablePos?.id}_discountCell`);
                this.editedCells.delete(`${editablePos?.id}_vatCell`);
                return { ...state };
              }),
            );
          }),
          switchMap((state) => {
            if (state.selectedPos?.quantity) {
              this.stream$.next({ ...state, action: 'updateState' });
              return this.getDeliveryCost({ state }); // Recalculate delivery data
            } else return of(state);
          }),
        );
      }),
    );
  }

  /* ORDER CELL METHODS end */

  /* UTILITY start */
  private defaultRound(v: number): number {
    const precision = companyConfigService.getCachedConfigs()?.decimalPlacesCount || 2;
    const multiplier = Math.pow(10, precision);
    return Math.round(v * multiplier) / multiplier;
  }

  private prepareArgsToSave({
    state,
    orderId,
    modificationOptions,
  }: IPrepareArgsToSave): SaveOrderMutationVariables {
    const {
      orderData: {
        specialAddress,
        referenceOrderNo,
        deliveryTime,
        linkText,
        note,
        note2,
        positions = [],
        orderType,
        customerId,
        orderDate,
      },
    } = state || {};
    const { specialAddress: prevSpecialAddress } =
      (state.orderDataBackup as IOrderDetailsState['orderData']) || {};

    const formattedPositions = positions.map((position) => ({
      virtualPositionId: position?.virtualPositionId,
      articleId: position?.articleId,
      quantity: position?.quantity || 0,
      price: position?.price,
      total: position?.total,
      discount: position?.discount,
      vatCode: position?.vatCode,
      indTextDeliveryNote: position?.indTextDeliveryNote,
      indTextProduction: position?.indTextProduction,
      splittingPartsPreset: position?.splittingPartsPreset,
    })) as Wa_OrderPositionInputs[];

    let orderProps = {
      id: String(orderId),
      customerId,
      date: format(localeFormatterHelper.localizedDate(orderDate), 'yyyy-MM-dd'),
      orderType,
      referenceOrderNo,
      deliveryTime,
      linkText,
      note,
      note2,
      notePlainText: null,
      positions: formattedPositions,
      modificationOptions: {
        ignoreMinMaxProblems: false,
        ignoreQuantityPerLotProblems: false,
        ignoreSplittingProblems: false,
        ignoreDaysToProduce: false,
        ...modificationOptions,
      },
    } as SaveOrderMutationVariables['orderProps'];

    if (prevSpecialAddress !== specialAddress) {
      orderProps = { ...orderProps, specialAddress };
    }

    return {
      orderProps,
    };
  }
  private calculateTotalDependsOnSaleUnit({
    price,
    quantity,
    saleUnit,
  }: {
    price?: number | null;
    quantity?: number | null;
    saleUnit?: C_Sale_Unit | null;
  }): number {
    price = price ? price : 0;
    quantity = quantity ? quantity : 0;
    const kiloAmountRepresentationId =
      companyConfigService.getCachedConfigs()?.kiloAmountRepresentationId;
    if (saleUnit === C_Sale_Unit.SU2_100G) {
      return this.defaultRound((quantity / 100) * price);
    }
    if (
      saleUnit === C_Sale_Unit.SU3_1000G &&
      kiloAmountRepresentationId === C_Kg_Representation.KGR2_1000_IS_1
    ) {
      return this.defaultRound((quantity / 1000) * price);
    }
    return this.defaultRound(quantity * price);
  }
  private getDeliveryCost(args: {
    state: IOrderDetailsState;
    force?: true;
  }): Observable<IOrderDetailsState> {
    const updatedState = structuredClone(args.state);
    const {
      customerInfo: { transportSector },
    } = updatedState;
    const isDeliveryPosition =
      args.force || !!updatedState.orderData.positions?.some((val) => val?.virtualPositionId);
    if (isDeliveryPosition) {
      const deliveryPosition = {
        virtualPositionId: C_Virtual_Position.VP2_DELIVERY_COST,
        quantity: 1,
        vatCode: C_Vat_Code.VT5_ZERO,
        price: 0,
        total: 0,
        weight: 0,
        id: Date.now(),
      } as IPosition;

      if (transportSector?.isPriceRateFixed) {
        const fixedPriceDeliveryPosition = {
          ...deliveryPosition,
          price: transportSector.priceRateFixed,
          total: transportSector.priceRateFixed,
        };
        const fixedPriceDeliveryPositionInd = updatedState.orderData.positions?.findIndex(
          (el) => el?.virtualPositionId,
        );
        if (
          typeof fixedPriceDeliveryPositionInd === 'number' &&
          fixedPriceDeliveryPositionInd !== -1
        ) {
          updatedState.orderData.positions?.splice(
            fixedPriceDeliveryPositionInd,
            1,
            fixedPriceDeliveryPosition,
          );
        } else updatedState.orderData.positions?.push(fixedPriceDeliveryPosition);
        return of(updatedState);
      }
      const totalWeight: number = getTotalWeight(updatedState.orderData.positions);
      if (!totalWeight || !updatedState.orderData.positions?.some((val) => val?.articleId)) {
        const deliveryPositionInd = updatedState.orderData.positions?.findIndex(
          (el) => el?.virtualPositionId,
        );
        if (typeof deliveryPositionInd === 'number' && deliveryPositionInd !== -1) {
          updatedState.orderData.positions?.splice(deliveryPositionInd, 1, deliveryPosition);
        } else updatedState.orderData.positions?.push(deliveryPosition);
        this.deliveryCostLoading$.next(false);
        return of(updatedState);
      }
      return orderListState.sub.sharedValues().pipe(
        tap(({ order }) => {
          this.deliveryCostLoading$.next(true);
          orderService.pub.orderDeliveryCost({
            customerId: order!.customerId,
            totalWeight,
          });
        }),
        mergeMap(() => {
          return orderService.sub.orderDeliveryCost().pipe(
            responseHandler<OrderDeliveryCostRes | 'serverError'>({
              errorReturnType: 'serverError',
            }),
            tap((value) => {
              if (value === 'serverError') this.deliveryCostLoading$.next(false);
            }),
            filter((value) => value !== 'serverError'),
            map((value) => {
              const cost = value as OrderDeliveryCostRes;
              const updatedDeliveryPosition = {
                ...deliveryPosition,
                price: cost,
                total: cost,
                weight: totalWeight,
              };
              const deliveryPositionInd = updatedState.orderData.positions?.findIndex(
                (el) => el?.virtualPositionId,
              );
              if (typeof deliveryPositionInd === 'number' && deliveryPositionInd !== -1) {
                updatedState.orderData.positions?.splice(
                  deliveryPositionInd,
                  1,
                  updatedDeliveryPosition,
                );
              } else updatedState.orderData.positions?.push(updatedDeliveryPosition);
              this.deliveryCostLoading$.next(false);
              return updatedState;
            }),
          );
        }),
      );
    }
    return of(updatedState);
  }
  private preUpdateCell(position: IOrderDetailsState['selectedPos']) {
    const state = this.stream$.getValue();
    if (position && state.orderData.positions) {
      state.orderData.positions = state.orderData.positions?.map((el) =>
        el?.id === position.id ? position : el,
      );
      this.stream$.next({ ...state, action: 'updateState' });
    }
  }
  /* UTILITY end */
}
